import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../tokens";
import { Icon } from "./Icon";

/**
 * Modal ligero (sin dependencias) usado por los dialogos About y
 * Feedback. Replica el patron de la referencia: overlay translucido con
 * desenfoque, tarjeta centrada sobre la superficie "container" y cierre
 * con Escape o clic fuera. El ancho se controla desde fuera con
 * `widthClass` para adaptarse a cada dialogo.
 */
interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Etiqueta accesible del dialogo. */
  ariaLabel: string;
  /** Clase de ancho/altura del contenedor (ej. "w-[min(460px,92vw)]"). */
  widthClass?: string;
  children: ReactNode;
}

export function Modal({
  open,
  onClose,
  ariaLabel,
  widthClass = "w-[min(460px,92vw)]",
  children,
}: ModalProps) {
  const { t } = useTranslation("common");
  // Cerrar con Escape mientras el dialogo este abierto.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "relative max-h-[94vh] overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-container)] shadow-2xl",
          widthClass,
        )}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t("close")}
          className="absolute right-3 top-3 z-10 inline-flex h-7 w-7 items-center justify-center rounded text-[var(--color-on-surface-variant)] transition-colors hover:bg-[var(--color-surface-variant)] hover:text-[var(--color-on-surface)]"
        >
          <Icon name="close" size={16} />
        </button>
        {children}
      </div>
    </div>
  );
}
